import React, { useEffect, useState, useContext } from "react";
import { Button, Table, Modal, Form, Spinner, Alert, Badge } from "react-bootstrap";
import { AuthContext } from "../../hooks/auth/AuthContext";
import Head from "../../components/layout/head/Head";
import { toast } from "react-toastify";
import api from "../../services/api";

const CertificadosPage = () => {
    const authContext = useContext(AuthContext);
    const { user } = authContext;

    const [certificados, setCertificados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [incluirInactivos, setIncluirInactivos] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        id: "",
        pathCert: "",
        passwordCert: "",
        activo: true,
    });

    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line
    }, [incluirInactivos]);

    const loadData = async () => {
        try {
            setLoading(true);
            const config = {
                headers: { 'Authorization': `Bearer ${user.token}` },
                params: { soloActivos: !incluirInactivos }
            };
            const response = await api.get("/api/tenant-certificates", config);
            setCertificados(response.data);
        } catch (error) {
            console.error("Error al cargar certificados:", error);
            toast.error("Error al cargar los certificados");
        } finally {
            setLoading(false);
        }
    };

    const handleShowModal = async (item = null) => {
        if (item) {
            setEditMode(true);
            setCurrentItem(item);
            setFormData({
                id: item.id,
                pathCert: item.pathCert || "",
                passwordCert: item.passwordCert || "",
                activo: item.activo !== undefined ? item.activo : true,
            });
        } else {
            setEditMode(false);
            setCurrentItem(null);
            setFormData({
                id: "",
                pathCert: "",
                passwordCert: "",
                activo: true,
            });
        }
        setFormErrors({});
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));

        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.pathCert || formData.pathCert.trim() === "") {
            errors.pathCert = "La ruta del certificado es obligatoria";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            const config = {
                headers: { 'Authorization': `Bearer ${user.token}` }
            };

            const dataToSend = {
                PathCert: formData.pathCert,
                PasswordCert: formData.passwordCert,
                Activo: formData.activo,
            };

            if (editMode) {
                dataToSend.Id = formData.id;
                await api.put(`/api/tenant-certificates/${formData.id}`, dataToSend, config);
                toast.success("Certificado actualizado correctamente");
            } else {
                await api.post("/api/tenant-certificates", dataToSend, config);
                toast.success("Certificado creado correctamente");
            }

            handleCloseModal();
            loadData();
        } catch (error) {
            console.error("Error al guardar certificado:", error);
            if (error.response?.data) {
                toast.error(typeof error.response.data === 'string' ? error.response.data : "Error al guardar el certificado");
            } else {
                toast.error("Error al guardar el certificado");
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <>
            <Head title="Configuración - Certificados Digitales" />
            <div className="container-fluid mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h2>Gestión de Certificados Digitales</h2>
                    <div className="d-flex gap-3 align-items-center">
                        <Form.Check
                            type="checkbox"
                            label="Incluir inactivos"
                            checked={incluirInactivos}
                            onChange={(e) => setIncluirInactivos(e.target.checked)}
                        />
                        <Button variant="primary" onClick={() => handleShowModal()}>
                            Nuevo Certificado
                        </Button>
                    </div>
                </div>

                <Alert variant="info" className="mb-3">
                    <strong>Importante:</strong> Los certificados digitales se utilizan para firmar los documentos electrónicos. 
                    Deben estar ubicados en el servidor en la carpeta <code>certs/[RUC-TENANT]/</code>
                </Alert>

                {certificados.length === 0 ? (
                    <Alert variant="warning">No hay certificados digitales registrados.</Alert>
                ) : (
                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th>Ruta del Certificado</th>
                                    <th>Tiene Password</th>
                                    <th>Estado</th>
                                    <th>Fecha Creación</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {certificados.map((item) => (
                                    <tr key={item.id}>
                                        <td><code>{item.pathCert}</code></td>
                                        <td>
                                            {item.passwordCert ? (
                                                <Badge bg="success">Sí</Badge>
                                            ) : (
                                                <Badge bg="secondary">No</Badge>
                                            )}
                                        </td>
                                        <td>
                                            <Badge bg={item.activo ? "success" : "secondary"}>
                                                {item.activo ? "Activo" : "Inactivo"}
                                            </Badge>
                                        </td>
                                        <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                                        <td>
                                            <Button
                                                variant="warning"
                                                size="sm"
                                                onClick={() => handleShowModal(item)}
                                            >
                                                Editar
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                {/* Modal para Crear/Editar */}
                <Modal show={showModal} onHide={handleCloseModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>{editMode ? "Editar Certificado" : "Nuevo Certificado"}</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Alert variant="info" className="mb-3">
                                <small>
                                    <strong>Instrucciones:</strong><br />
                                    1. Copia el archivo .p12 o .pfx a la carpeta del servidor: <code>certs/[RUC-TENANT]/</code><br />
                                    2. Ingresa la ruta relativa del certificado (ej: <code>certificado.p12</code>)<br />
                                    3. Ingresa la contraseña si el certificado está protegido
                                </small>
                            </Alert>

                            <Form.Group className="mb-3">
                                <Form.Label>Ruta del Certificado *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="pathCert"
                                    value={formData.pathCert}
                                    onChange={handleInputChange}
                                    isInvalid={!!formErrors.pathCert}
                                    placeholder="certificado.p12"
                                />
                                <Form.Text className="text-muted">
                                    Nombre del archivo dentro de la carpeta certs/[RUC-TENANT]/
                                </Form.Text>
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.pathCert}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Contraseña del Certificado</Form.Label>
                                <Form.Control
                                    type="password"
                                    name="passwordCert"
                                    value={formData.passwordCert}
                                    onChange={handleInputChange}
                                    placeholder="Dejar vacío si no tiene contraseña"
                                />
                                <Form.Text className="text-muted">
                                    Contraseña para abrir el archivo del certificado (si aplica)
                                </Form.Text>
                            </Form.Group>

                            {editMode && (
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        name="activo"
                                        label="Activo"
                                        checked={formData.activo}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseModal} disabled={submitting}>
                                Cancelar
                            </Button>
                            <Button variant="primary" type="submit" disabled={submitting}>
                                {submitting ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                                        Guardando...
                                    </>
                                ) : editMode ? (
                                    "Actualizar"
                                ) : (
                                    "Crear"
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </div>
        </>
    );
};

export default CertificadosPage;
